const MAPBOX = import.meta.env.VITE_MAPBOX_TOKEN;
const terrainTileCache = new Map();

function haversineMeters(a, b) {
  const toRad = (value) => (Number(value) * Math.PI) / 180;
  const lat1 = toRad(a?.lat);
  const lat2 = toRad(b?.lat);
  const dLat = lat2 - lat1;
  const dLon = toRad(Number(b?.lon) - Number(a?.lon));
  if (![lat1, lat2, dLat, dLon].every(Number.isFinite)) return 0;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
}

function getTileCoords(lat, lon, z) {
  const tileX = Math.floor(((lon + 180) / 360) * 2 ** z);
  const tileY = Math.floor(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      2 ** z
  );
  return { tileX, tileY };
}

async function loadTerrainTile(z, tileX, tileY) {
  const key = `${z}/${tileX}/${tileY}`;
  if (terrainTileCache.has(key)) return terrainTileCache.get(key);

  const url = `https://api.mapbox.com/v4/mapbox.terrain-rgb/${z}/${tileX}/${tileY}.pngraw?access_token=${MAPBOX}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("tile_fetch_failed");

  const blob = await res.blob();
  let bitmap = null;
  if ("createImageBitmap" in window) {
    bitmap = await createImageBitmap(blob);
  } else {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);
    bitmap = await new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
  }

  const canvas =
    typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(bitmap.width, bitmap.height)
      : Object.assign(document.createElement("canvas"), {
          width: bitmap.width,
          height: bitmap.height,
        });
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0);

  const entry = { canvas, ctx, size: bitmap.width };
  terrainTileCache.set(key, entry);
  return entry;
}

async function getElevationAt(lat, lon, z = 14) {
  if (!MAPBOX) return null;
  const { tileX, tileY } = getTileCoords(lat, lon, z);
  const tile = await loadTerrainTile(z, tileX, tileY);
  const scale = 2 ** z * 256;
  const px = Math.round(((lon + 180) / 360) * scale - tileX * 256);
  const py = Math.round(
    ((1 -
      Math.log(
        Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
      ) /
        Math.PI) /
      2) *
      scale -
      tileY * 256
  );
  const { data } = tile.ctx.getImageData(px, py, 1, 1);
  if (data[0] === 0 && data[1] === 0 && data[2] === 0) return null;
  const elevation =
    -10000 + (data[0] * 256 ** 2 + data[1] * 256 + data[2]) * 0.1;
  if (!Number.isFinite(elevation)) return null;
  return Math.round(elevation);
}

export async function getElevationProfile(points, opts = {}) {
  if (!MAPBOX) return null;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return null;
  if (!Array.isArray(points) || points.length < 2) return null;

  const total = points.length;
  const maxSamples = Math.max(2, Number(opts.maxSamples) || 1600);
  const targetSpacingMeters = Math.max(50, Number(opts.targetSpacingMeters) || 250);
  const samples = [0];
  let distanceSinceSample = 0;
  for (let i = 1; i < total - 1; i += 1) {
    distanceSinceSample += haversineMeters(points[i - 1], points[i]);
    if (distanceSinceSample >= targetSpacingMeters && samples.length < maxSamples - 1) {
      samples.push(i);
      distanceSinceSample = 0;
    }
  }
  if (samples[samples.length - 1] !== total - 1) {
    samples.push(total - 1);
  }

  const elevByIdx = {};
  let lastValid = null;
  let validSamples = 0;
  for (const idx of samples) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return null;
    }
    const p = points[idx];
    if (!Number.isFinite(p?.lat) || !Number.isFinite(p?.lon)) {
      elevByIdx[idx] = Number.isFinite(lastValid) ? lastValid : null;
      continue;
    }
    const ele = await getElevationAt(p.lat, p.lon, opts.zoom || 14);
    let next = Number.isFinite(ele) ? ele : null;
    if (Number.isFinite(next)) validSamples += 1;
    if (next == null && Number.isFinite(lastValid)) next = lastValid;
    if (Number.isFinite(next)) lastValid = next;
    elevByIdx[idx] = next;
  }

  if (validSamples === 0) return null;

  const filled = new Array(total).fill(0);
  let lastIdx = samples[0];
  const startSeed = elevByIdx[lastIdx];
  filled[lastIdx] = Number.isFinite(startSeed) ? startSeed : 0;
  for (let s = 1; s < samples.length; s++) {
    const nextIdx = samples[s];
    const startRaw = elevByIdx[lastIdx];
    const endRaw = elevByIdx[nextIdx];
    const startEle = Number.isFinite(startRaw)
      ? startRaw
      : Number.isFinite(endRaw)
      ? endRaw
      : 0;
    const endEle = Number.isFinite(endRaw) ? endRaw : startEle;
    const span = nextIdx - lastIdx || 1;
    for (let i = lastIdx; i <= nextIdx; i++) {
      const t = (i - lastIdx) / span;
      filled[i] = Math.round(startEle + (endEle - startEle) * t);
    }
    lastIdx = nextIdx;
  }

  return points.map((p, idx) => ({
    ...p,
    ele: filled[idx],
  }));
}
